/**
 * AdminStorylinePanel React component
 * 
 * @author    Jens Gruschel
 * @copyright © 2019 contexagon GmbH
 */


// React
import React from 'react';
import connect from "react-redux/es/connect/connect";

// Material UI
import { withStyles } from '@material-ui/core/styles';

// Material UI components
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from "@material-ui/core/Tooltip";
import Link from '@material-ui/core/Link';

// Material UI icons
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CancelIcon from '@material-ui/icons/Cancel';

// application components
import WarningPanel from "../base/WarningPanel";

// application
import {fetchJSON} from "../../operations/communication";
import {
    REST_CALL_UPDATE_SERVER_STORYLINES,
    REST_CALL_CANCEL_STORYLINE,
    REST_SERVER_BASE_URL
} from "../../config";
import {editObject} from "../../operations/editing";


/**
 * Panel for admin page to show storylines fetched from server
 *
 * @author Jens Gruschel
 */
class AdminStorylinePanel extends React.Component {

    state = {
        disconnected: false,
        storylines: []
    };

    componentDidMount() {
        this.refreshData();
        this.timer = setInterval(() => {this.refreshData()}, 5000);
    }

    componentWillUnmount() {
        clearInterval(this.timer);
    }

    refreshData() {
        this.refreshStorylines();
    }

    async refreshStorylines() {
        
        const result = await this.fetchServerStorylines();

        if (result) {
            const storylines = result.storylines || [];
            this.setState({
                disconnected: false,
                storylines: storylines.sort((a, b) => a.storyline.localeCompare(b.storyline))
            });
        }
        else {
            this.setState({
                disconnected: true
            });
        }
    }

    render() {

        const { classes } = this.props;

        return <div className={classes.panel}>

            {
                this.state.disconnected && <WarningPanel text="Verbindung zum Server unterbrochen!" />
            }

            <ExpansionPanel defaultExpanded={true}>

                <ExpansionPanelSummary expandIcon={<ExpandMoreIcon/>}>
                    <Typography>laufende Szenen</Typography>
                </ExpansionPanelSummary>

                <ExpansionPanelDetails>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Storyline</TableCell>
                                <TableCell>Zielgeräte</TableCell>
                                <TableCell align="right">anstehend</TableCell>
                                <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {this.state.storylines.map((storyline, index) => {
                                return <TableRow key={storyline.storyline}>
                                    <TableCell>
                                        {storyline.storyline}
                                    </TableCell>
                                    <TableCell>
                                        {
                                            [
                                                ...storyline.targets.map(target =>
                                                    <Link key={target.id} component="button" style={{fontSize: "13px"}} onClick={() => {this.props.editObject(target.id);}}>
                                                        {target.name}
                                                    </Link>
                                                ),
                                                ...storyline.mobiles.map(mobile =>
                                                    <Typography>{mobile}</Typography>
                                                )
                                            ].reduce((accu, element) => accu ? [...accu, ", ", element] : [element], null) // join separated by comma
                                        }
                                    </TableCell>
                                    <TableCell align="right">
                                        {storyline.actions} Aktion(en)
                                    </TableCell>
                                    <TableCell className={classes.rowActionCell} align="right">
                                        <Tooltip title="Abbrechen">
                                            <IconButton
                                                onClick={() => this.cancelStoryline(storyline.storyline)}
                                            >
                                                <CancelIcon/>
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>;
                            })}
                        </TableBody>
                    </Table>
                </ExpansionPanelDetails>

            </ExpansionPanel>

        </div>;
    }

    async fetchServerStorylines() {
        try {
            const result = await fetchJSON(
                REST_SERVER_BASE_URL + REST_CALL_UPDATE_SERVER_STORYLINES.path, {
                    method: REST_CALL_UPDATE_SERVER_STORYLINES.method,
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + this.props.accessToken
                    }
                }
            );
            return result;
        }
        catch (error) {
            return null;
        }
    }
    
    async cancelStoryline(storyline) {
        try {
            await fetchJSON(
                REST_SERVER_BASE_URL + REST_CALL_CANCEL_STORYLINE.path.replace("<id>", storyline), {
                    method: REST_CALL_CANCEL_STORYLINE.method,
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + this.props.accessToken
                    }
                }
            );
            this.refreshData();
            return true;
        }
        catch (error) {
            return false;
        }
    }
}

const styles = theme => ({
    panel: {
        width: '100%'
    }
});

const mapStateToProps = state => {
    return {
        accessToken: state.authorization.accessToken
    };
};

export default connect(mapStateToProps, {editObject})(withStyles(styles)(AdminStorylinePanel));